import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@clerk/react";
import { useDashboardStore } from "@/stores";
import { useChatStore } from "@/stores/chat/chatStore";
import { Composer } from "./Composer";
import { answerableQuestions, type ThreadLogEvent } from "./projection";
import { AskContext, type AskContextValue } from "./asking";
import { ThreadController, type ThreadCallbacks, type ThreadCursor } from "./threadController";
import { Transcript } from "./Transcript";

/**
 * The eve Amy service host. Prod: the Railway `amy` service; local: dev.sh
 * runs it on :2000.
 */
const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

interface AmyThreadProps {
  conversationId: string;
  /** Shared by a teammate: render only, never send or reconnect. */
  readOnly: boolean;
  initialSession: ThreadCursor | null;
  initialEvents: ThreadLogEvent[];
}

/**
 * One open conversation with Amy. The parent remounts it per conversation
 * (`key={chatId}`), so each thread gets its own controller seeded with that
 * thread's persisted cursor + event log.
 */
export function AmyThread({ conversationId, readOnly, initialSession, initialEvents }: AmyThreadProps) {
  const { getToken, orgId } = useAuth();
  const saveEveState = useChatStore((s) => s.saveEveState);

  const callbacks: ThreadCallbacks = {
    save: async ({ session, events }) => {
      const token = await getToken();
      if (!token) return false;
      return saveEveState(conversationId, token, { session, events });
    },
    // Amy may have created or changed campaigns — refresh the list the nav
    // and @-mentions read from.
    onSettled: () => {
      if (!orgId) return;
      void (async () => {
        const token = await getToken();
        if (token) await useDashboardStore.getState().fetchCampaigns(orgId, token);
      })();
    },
    onFirstMessageSent: () => useChatStore.getState().clearPendingFirstMessage(),
  };

  const [controller] = useState(() => {
    // A chat created from the empty state queues its first message in the store.
    const { pendingFirstMessage, activeId } = useChatStore.getState();
    return new ThreadController({
      host: EVE_HOST,
      getToken,
      initialSession,
      initialEvents,
      readOnly,
      firstMessage: !readOnly && activeId === conversationId ? pendingFirstMessage : null,
      ...callbacks,
    });
  });
  useEffect(() => controller.setCallbacks(callbacks));
  useEffect(() => {
    controller.attach();
    return () => controller.detach();
  }, [controller]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const { projection, status, answering } = snapshot;

  // Parked questions are answered in their questionnaire / approval card;
  // the composer waits until they are.
  const parked = useMemo(() => answerableQuestions(projection), [projection]);

  const ask = useMemo<AskContextValue>(
    () => ({
      canAnswer: !readOnly && status === "idle",
      readOnly,
      answering,
      keyboardTarget: parked[0]?.id,
      onAnswer: (requestId, response) => void controller.answer(requestId, response),
      onAnswerMany: (responses) => void controller.answerMany(responses),
    }),
    [readOnly, status, answering, parked, controller]
  );

  const notices = [
    snapshot.error,
    snapshot.persistFailed
      ? "This chat isn't being saved to your history. Start a new chat to keep a saved transcript."
      : null,
  ].filter((n): n is string => !!n);

  return (
    <AskContext.Provider value={ask}>
      <Transcript
        items={projection.items}
        status={status}
        outgoing={snapshot.outgoing}
        notices={notices}
        onRetry={() => void controller.retry()}
      />
      {readOnly ? (
        <div className="mx-auto w-full max-w-3xl px-4 pt-1 pb-4">
          <Alert variant="neutral">
            <AlertTitle className="justify-center">Shared with you by a teammate · read-only</AlertTitle>
          </Alert>
        </div>
      ) : (
        <Composer
          busy={status !== "idle"}
          blocked={parked.length > 0}
          placeholder={
            parked.length > 1
              ? "Answer the questions above to continue"
              : parked.length === 1
                ? "Answer the question above to continue"
                : undefined
          }
          onSend={(text) => void controller.send(text)}
          onStop={() => void controller.stop()}
        />
      )}
    </AskContext.Provider>
  );
}
